import {
    buildGroupMembershipInviteProfileIds,
    expandSelectedUserIdsWithAgents,
} from "./useNewMembershipAgentSelection.js";
import { getLiveConversationParticipantIds } from "./useNewMembershipData.js";

/**
 * Resolve whether an existing resource member should stay in invited_profiles
 * as a conversation participant when building restore/invite payloads.
 *
 * Archived conversations load is_conversation_participant as false for everyone,
 * but restore rehydrates live participants on the conversation object before invite.
 */
export function resolveExistingMemberInviteParticipation({
    invitationStore,
    roleProfile,
    liveParticipantIds,
}) {
    if (invitationStore.unselected_ids_for_remove?.has?.(roleProfile.id)) {
        return false;
    }

    if (
        invitationStore.temp_chat_access_status?.[roleProfile.id] === true &&
        !roleProfile.is_conversation_participant
    ) {
        return false;
    }

    if (liveParticipantIds.has(Number(roleProfile.id))) {
        return true;
    }

    return Boolean(roleProfile.is_conversation_participant);
}

/**
 * Apply live conversation participation onto role profiles.
 * Agents always mirror their human owner so display-only agent hiding cannot
 * clear is_conversation_participant and drop agents from invite payloads.
 *
 * @param {object} params
 * @param {Array} params.roleProfiles
 * @param {Set<number>} params.liveParticipantIds
 * @param {(profileId: number|string, isParticipant: boolean) => void} params.updateParticipantAccess
 */
export function syncRoleProfilesConversationParticipation({
    roleProfiles,
    liveParticipantIds,
    updateParticipantAccess,
}) {
    for (const roleProfile of roleProfiles ?? []) {
        if (roleProfile?.is_agent) {
            continue;
        }

        const isParticipant = liveParticipantIds.has(Number(roleProfile.id));

        updateParticipantAccess(roleProfile.id, isParticipant);

        if (roleProfile.agent_id) {
            updateParticipantAccess(roleProfile.agent_id, isParticipant);
        }
    }
}

/**
 * Keep agent is_conversation_participant aligned with their human owner.
 * Optionally create missing agent role-profile rows so invite payloads include them
 * the same way group/topic invites include agent IDs.
 *
 * @param {object} params
 * @param {Array} params.roleProfiles
 * @param {(profileId: number|string, isParticipant: boolean) => void} params.updateParticipantAccess
 * @param {(agentProfile: object) => void} [params.ensureAgentRoleProfile]
 * @param {(agentId: number|string) => object|null|undefined} [params.resolveAgentProfile]
 * @param {string} [params.defaultRole]
 */
export function mirrorHumanAgentConversationParticipation({
    roleProfiles,
    updateParticipantAccess,
    ensureAgentRoleProfile = null,
    resolveAgentProfile = null,
    defaultRole = "Member",
}) {
    const existingIds = new Set(
        (roleProfiles ?? []).map((profile) => Number(profile?.id)),
    );

    for (const roleProfile of roleProfiles ?? []) {
        if (roleProfile?.is_agent || !roleProfile?.agent_id) {
            continue;
        }

        const isParticipant = Boolean(roleProfile.is_conversation_participant);
        const agentId = roleProfile.agent_id;

        if (!existingIds.has(Number(agentId)) && ensureAgentRoleProfile) {
            const resolvedAgent = resolveAgentProfile?.(agentId);

            ensureAgentRoleProfile({
                id: agentId,
                is_agent: true,
                owner_id: roleProfile.id,
                role: roleProfile.role ?? defaultRole,
                is_conversation_participant: isParticipant,
                has_parent_access: false,
                first_name: resolvedAgent?.first_name,
                full_name: resolvedAgent?.full_name,
                email: resolvedAgent?.email,
            });
            existingIds.add(Number(agentId));
            continue;
        }

        updateParticipantAccess(agentId, isParticipant);
    }
}

export function isExistingWorkPanelResourceMember(invitationStore, profileId) {
    return (
        invitationStore.current_user_ids?.includes?.(profileId) ?? false
    );
}

/**
 * Toggle conversation access for a human and their agent without removing role rows.
 *
 * @param {object} params
 * @param {object} params.invitationStore
 * @param {object} params.profile
 * @param {number|string|null|undefined} [params.agentId]
 * @param {boolean} params.isParticipant
 */
export function setWorkPanelMemberConversationParticipation({
    invitationStore,
    profile,
    agentId = null,
    isParticipant,
}) {
    if (!profile?.id) {
        return;
    }

    invitationStore.updateParticipantAccess(profile.id, isParticipant);

    const resolvedAgentId =
        agentId ?? profile.agent_id ?? null;

    if (!profile.is_agent && resolvedAgentId) {
        invitationStore.updateParticipantAccess(resolvedAgentId, isParticipant);
    }
}

/**
 * Expand selected IDs with agent IDs using both user store and invitation role rows.
 *
 * @param {Array<number|string>} userIds
 * @param {object} userStore
 * @param {object} invitationStore
 */
export function expandWorkPanelSelectedUserIdsWithAgents(
    userIds,
    userStore,
    invitationStore,
) {
    const expandedIds = new Set(
        expandSelectedUserIdsWithAgents(userIds, userStore),
    );

    for (const userId of [...expandedIds]) {
        const storeProfile = invitationStore.user_profiles_with_roles?.find(
            (profile) => Number(profile?.id) === Number(userId),
        );
        const agentId =
            userStore.getUserProfileById(userId)?.agent_id ??
            storeProfile?.agent_id;

        if (agentId != null) {
            expandedIds.add(agentId);
        }
    }

    return [...expandedIds];
}

export function isArchivedWorkPanelConversation(activeConversation) {
    return Boolean(
        activeConversation?.is_archived ||
        activeConversation?.isArchived?.(),
    );
}

export function syncWorkPanelCurrentParticipantsFromRoleProfiles(
    invitationStore,
) {
    invitationStore.current_participants =
        invitationStore.user_profiles_with_roles
            ?.filter((roleProfile) => roleProfile.is_conversation_participant)
            .map((roleProfile) => roleProfile.id) ?? [];
}

/**
 * Reconcile stale membership API participation flags before restore/invite on an
 * archived work panel conversation. The invites API marks everyone as
 * non-participants, but live archived participants remain on the conversation
 * object. Do not call this when opening the membership panel for display; chin
 * avatars should reflect the API flags until invite/restore runs.
 *
 * @param {object} params
 * @param {object} params.invitationStore
 * @param {object} params.userStore
 * @param {object|null} [params.activeConversation]
 */
export function reconcileArchivedWorkPanelMembershipParticipation({
    invitationStore,
    userStore,
    activeConversation = null,
}) {
    if (!activeConversation || !isArchivedWorkPanelConversation(activeConversation)) {
        return;
    }

    prepareWorkPanelInviteParticipationFlags({
        invitationStore,
        userStore,
        activeConversation,
    });

    syncWorkPanelCurrentParticipantsFromRoleProfiles(invitationStore);
}

/**
 * Normalize invite participation flags before restore/invite requests.
 * New invitees default to participants but keep any explicit chat-off toggle.
 * Existing members keep their current chat-access state so members with chat
 * turned off are not re-added. After an
 * archived conversation is restored, live participants are preferred over stale
 * membership API flags so existing members are not dropped from invited_profiles.
 *
 * @param {object} params
 * @param {object} params.invitationStore
 * @param {object} params.userStore
 * @param {object|null} [params.activeConversation]
 */
export function prepareWorkPanelInviteParticipationFlags({
    invitationStore,
    userStore,
    activeConversation = null,
}) {
    const liveParticipantIds =
        getLiveConversationParticipantIds(activeConversation);

    for (const roleProfile of invitationStore.user_profiles_with_roles ?? []) {
        if (!roleProfile?.id || roleProfile.is_agent) {
            continue;
        }

        if (invitationStore.unselected_ids_for_remove?.has?.(roleProfile.id)) {
            setWorkPanelMemberConversationParticipation({
                invitationStore,
                profile: roleProfile,
                isParticipant: false,
            });
            continue;
        }

        const isExistingMember = isExistingWorkPanelResourceMember(
            invitationStore,
            roleProfile.id,
        );
        const isNewInvite = invitationStore.selected_ids_for_invite?.has?.(
            roleProfile.id,
        );

        if (isNewInvite) {
            setWorkPanelMemberConversationParticipation({
                invitationStore,
                profile: roleProfile,
                isParticipant: Boolean(roleProfile.is_conversation_participant),
            });
            continue;
        }

        if (isExistingMember) {
            setWorkPanelMemberConversationParticipation({
                invitationStore,
                profile: roleProfile,
                isParticipant: resolveExistingMemberInviteParticipation({
                    invitationStore,
                    roleProfile,
                    liveParticipantIds,
                }),
            });
            continue;
        }

        if (roleProfile.is_conversation_participant) {
            setWorkPanelMemberConversationParticipation({
                invitationStore,
                profile: roleProfile,
                isParticipant: true,
            });
        }
    }

    ensureWorkPanelAgentParticipationInInvitationStore({
        invitationStore,
        userStore,
        defaultRole: invitationStore.ROLES?.MEMBER ?? "member",
    });
}

/**
 * Keep agent rows aligned in the invitation store before building invite payloads.
 *
 * @param {object} params
 * @param {object} params.invitationStore
 * @param {object} params.userStore
 * @param {string} [params.defaultRole]
 */
export function ensureWorkPanelAgentParticipationInInvitationStore({
    invitationStore,
    userStore,
    defaultRole = "member",
}) {
    mirrorHumanAgentConversationParticipation({
        roleProfiles: invitationStore.user_profiles_with_roles,
        updateParticipantAccess: (profileId, isParticipant) =>
            invitationStore.updateParticipantAccess(profileId, isParticipant),
        ensureAgentRoleProfile: (agentProfile) => {
            invitationStore.user_profiles_with_roles.push({
                id: agentProfile.id,
                role: agentProfile.role ?? defaultRole,
                is_agent: true,
                owner_id: agentProfile.owner_id,
                is_conversation_participant:
                    agentProfile.is_conversation_participant,
                has_parent_access: false,
                first_name: agentProfile.first_name,
                full_name: agentProfile.full_name,
                email: agentProfile.email,
            });
        },
        resolveAgentProfile: (agentId) =>
            userStore.getAgentProfileById(agentId),
        defaultRole,
    });
}

/**
 * Build work panel invite profile IDs with agent IDs included, matching group/topic.
 *
 * @param {object} params
 * @param {Array<number|string>} params.selectedUserIds
 * @param {object} params.userStore
 * @param {object} params.activeConversation
 * @param {object} params.invitationStore
 */
export function buildWorkPanelMembershipInviteProfileIds({
    selectedUserIds,
    userStore,
    activeConversation,
    invitationStore,
}) {
    prepareWorkPanelInviteParticipationFlags({
        invitationStore,
        userStore,
        activeConversation,
    });

    const expandedSelectedUserIds = expandWorkPanelSelectedUserIdsWithAgents(
        selectedUserIds,
        userStore,
        invitationStore,
    );

    const inviteProfileIds = buildGroupMembershipInviteProfileIds({
        selectedUserIds: expandedSelectedUserIds,
        userStore,
        activeConversation,
    });

    const participatingProfileIds =
        invitationStore.user_profiles_with_roles
            ?.filter(
                (profile) =>
                    profile?.is_conversation_participant ||
                    profile?.role === invitationStore.ROLES?.OBSERVER,
            )
            .map((profile) => profile.id) ?? [];

    return [...new Set([...inviteProfileIds, ...participatingProfileIds])];
}
